import { useEffect, useRef, useState } from 'react'
import type { JSX, RefObject } from 'react'
import { Box } from '@chakra-ui/react';
import type { ScriptLane } from '../../types/script'
import { useIsMobile } from '../../hooks/useIsMobile'
import { getBranchDestination } from './branchDestination'
import { assignWireLanes } from './branchWireRouter'
import type { WireSpec } from './branchWireRouter'

export function BranchWireOverlay({
  lanes, containerRef, enabled,
}: { lanes: ScriptLane[]; containerRef: RefObject<HTMLElement | null>; enabled: boolean }) {
  const isMobile = useIsMobile()
  const svgRef = useRef<SVGSVGElement>(null)
  const [, force] = useState(0)

  const active = enabled && !isMobile
  useEffect(() => {
    if (!active) return
    const redraw = () => force((n) => n + 1)
    const ro = new ResizeObserver(redraw)
    if (containerRef.current) ro.observe(containerRef.current)
    window.addEventListener('resize', redraw)
    return () => { ro.disconnect(); window.removeEventListener('resize', redraw) }
  }, [active, containerRef])

  if (!active) return null

  // Build wire specs from branch lanes (end vs rejoin), route lanes, then measure + draw.
  const specs: WireSpec[] = lanes
    .filter((l) => l.id !== 'main' && l.sourceElementClientId)
    .map((l) => {
      const dest = getBranchDestination(l, lanes)
      return dest.kind === 'rejoin'
        ? { id: l.id, sourceClientId: l.id, kind: 'rejoin' as const, targetClientId: dest.targetClientId }
        : { id: l.id, sourceClientId: l.id, kind: 'end' as const }
    })
  // 4 lanes fit inside the reserved 74px gutter at 16px spacing; overflow wires are
  // dropped by the router rather than drawn past the container's right edge.
  const { routed } = assignWireLanes(specs, { maxLanes: 4 })

  const root = containerRef.current
  const paths: JSX.Element[] = []
  if (root) {
    const base = root.getBoundingClientRect()
    const colRight = base.width - 74
    for (const wire of routed) {
      const srcEl = root.querySelector(`[data-lane-id="${wire.id}"] [data-testid="branch-lane-header"]`)
      if (!srcEl) continue
      const s = srcEl.getBoundingClientRect()
      const sx = s.right - base.left
      const sy = s.top - base.top + s.height / 2
      const laneX = colRight + 15 + wire.laneIndex * 16
      // Color each wire to match its source branch's hue so a wire is easy to trace
      // back to the branch node it leaves. Markers (arrow/square) use context-stroke
      // and inherit this per-path color automatically.
      const srcLane = lanes.find((l) => l.id === wire.id)
      const stroke = `var(--chakra-colors-${srcLane?.colorScheme ?? 'gray'}-400)`
      if (wire.kind === 'rejoin' && wire.targetClientId) {
        const tgt = root.querySelector(`[data-client-id="${wire.targetClientId}"]`)
        if (!tgt) continue
        const t = tgt.getBoundingClientRect()
        const tx = t.right - base.left
        const ty = t.top - base.top + t.height / 2
        paths.push(
          <path key={wire.id} d={`M ${sx} ${sy} L ${laneX} ${sy} L ${laneX} ${ty} L ${tx + 2} ${ty}`}
            fill="none" stroke={stroke} strokeWidth={2} strokeDasharray="2 5.5"
            strokeLinecap="round" markerEnd="url(#sbw-arrow)" />,
        )
      } else {
        paths.push(
          <path key={wire.id} d={`M ${sx} ${sy} L ${laneX} ${sy} L ${laneX} ${sy + 22}`}
            fill="none" stroke={stroke} strokeWidth={2} strokeDasharray="2 5.5"
            strokeLinecap="round" markerEnd="url(#sbw-square)" />,
        )
      }
    }
  }

  return (
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      overflow="visible"
      zIndex={3}
      aria-hidden
      asChild><svg ref={svgRef}>
        <defs>
          <marker id="sbw-arrow" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="context-stroke" />
          </marker>
          <marker id="sbw-square" markerWidth="7" markerHeight="7" refX="3" refY="3">
            <rect x="0.5" y="0.5" width="5" height="5" fill="context-stroke" />
          </marker>
        </defs>
        {paths}
      </svg></Box>
  );
}
